import React from "react";
import { Button, Grid, Typography, Paper, Box } from "@material-ui/core";
import { useRhfFieldWithoutControllerOrRegister } from "customHooks/useCompleoReactHookForm/helpers/reactHookFormsHelper";
import { useTranslation } from "react-i18next";
import {
    ILocalAssessment,
    ILocalTest,
    createNewAssessment,
    addTestToAssessment,
    removeTestFromAssessment,
    updateAssessment,
    deleteAssessment,
    cleanupInvalidStageRulesFromAssessments
} from "./helpers";
import AssessmentCard from "./AssessmentCard";
import AssessmentModal from "./AssessmentModal";
import TestsBetaWarning from "Pages/Test/components/TestsBetaWarning";

const AssessmentsManager = () => {
    const { watch, setFieldValue } = useRhfFieldWithoutControllerOrRegister(
        "assessments.value"
    );
    const assessmentsValue: ILocalAssessment[] =
        watch("assessments.value") || [];

    // Watch pipeline to get current stages
    const { watch: watchPipeline } = useRhfFieldWithoutControllerOrRegister(
        "pipeline"
    );
    const pipelineValue = watchPipeline("pipeline");
    const pipelineStages = pipelineValue?.stages || [];

    const [tLocal] = useTranslation(["JOBVIEWASSESSMENTS", "TESTNEW"], {
        useSuspense: false
    });

    const [modalOpen, setModalOpen] = React.useState(false);
    const [editingAssessment, setEditingAssessment] = React.useState<
        ILocalAssessment | undefined
    >(undefined);

    // Automatically clean up invalid stage rules when pipeline stages change
    React.useEffect(() => {
        if (assessmentsValue.length > 0 && pipelineStages.length > 0) {
            const cleaned = cleanupInvalidStageRulesFromAssessments(
                assessmentsValue,
                pipelineStages
            );
            // Only update if something actually changed
            if (JSON.stringify(cleaned) !== JSON.stringify(assessmentsValue)) {
                setFieldValue(cleaned);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pipelineStages]); // Run when pipeline stages change, not when assessments change

    const handleNewAssessment = () => {
        setEditingAssessment(undefined);
        setModalOpen(true);
    };

    const handleEditAssessment = (assessment: ILocalAssessment) => {
        setEditingAssessment(assessment);
        setModalOpen(true);
    };

    const handleSaveAssessment = (
        assessmentData: Partial<ILocalAssessment>
    ) => {
        if (editingAssessment) {
            // Update existing assessment
            const updated = updateAssessment(
                assessmentsValue,
                editingAssessment.assessmentId,
                assessmentData
            );
            setFieldValue(updated);
        } else {
            // Create new assessment
            const newAssessment = createNewAssessment(
                assessmentData.name || "",
                assessmentData.resultsToStageRule || []
            );
            setFieldValue([...assessmentsValue, newAssessment]);
        }
    };

    const handleDeleteAssessment = (assessmentId: string) => {
        const updated = deleteAssessment(assessmentsValue, assessmentId);
        setFieldValue(updated);
    };

    const handleAddTest = (assessmentId: string, test: ILocalTest) => {
        const updated = addTestToAssessment(
            assessmentsValue,
            assessmentId,
            test
        );
        setFieldValue(updated);
    };

    const handleDeleteTest = (assessmentId: string, testId: string) => {
        const updated = removeTestFromAssessment(
            assessmentsValue,
            assessmentId,
            testId
        );
        setFieldValue(updated);
    };

    return (
        <Box
            style={{
                display: "flex",
                flexGrow: 1,
                flexDirection: "column"
            }}
        >
            <Paper style={{ margin: 16, padding: 16 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={8}>
                        <Typography variant="body1">
                            {tLocal("title")}
                        </Typography>
                    </Grid>
                    <Grid item xs sm container justify="flex-end">
                        <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={handleNewAssessment}
                        >
                            {tLocal("buttonNew")}
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <TestsBetaWarning />
                    </Grid>
                </Grid>
            </Paper>
            <Paper style={{ margin: 16, padding: 16, flexGrow: 1 }}>
                {assessmentsValue.length === 0 ? (
                    <Typography color="secondary">
                        {tLocal("noDataFound")}
                    </Typography>
                ) : (
                    <Grid container spacing={2}>
                        {assessmentsValue.map((assessment) => (
                            <Grid item xs={12} key={assessment.assessmentId}>
                                <AssessmentCard
                                    assessment={assessment}
                                    onEdit={() =>
                                        handleEditAssessment(assessment)
                                    }
                                    onDelete={() =>
                                        handleDeleteAssessment(
                                            assessment.assessmentId
                                        )
                                    }
                                    onAddTest={(test) =>
                                        handleAddTest(
                                            assessment.assessmentId,
                                            test
                                        )
                                    }
                                    onDeleteTest={(testId) =>
                                        handleDeleteTest(
                                            assessment.assessmentId,
                                            testId
                                        )
                                    }
                                    tLocal={tLocal}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Paper>
            {modalOpen && (
                <AssessmentModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    onSave={handleSaveAssessment}
                    editData={editingAssessment}
                    pipelineStages={pipelineStages}
                    tLocal={tLocal}
                />
            )}
        </Box>
    );
};

export default AssessmentsManager;
